import { useEffect, useRef, useState } from 'react';
import {
  cardActions,
  effectiveRoster,
  formatDayShort,
  formatMonthDay,
  formatTimeRange,
} from '@topspin/shared';
import type { CardAction, Session } from '@topspin/shared';
import { useDeleteOut, useSetAvailability, type GroupView, type SeasonView } from '../data.js';
import { useOnline } from '../hooks.js';
import { matchStatus, overrideFor, subCommitmentFor } from '../session-utils.js';
import AddSubFlow from './AddSubFlow.js';
import OutFlow from './OutFlow.js';

/**
 * One match, in full (§6.2). Everything a player might want to know about the night is on the
 * face of the card — who's on court, who isn't, and why — so the only reason to tap anything is
 * to change something. That's what the ⋯ menu is for, and it's the only control on the card.
 */
export default function MatchCard({
  view,
  group,
  session,
  playerId,
}: {
  view: SeasonView;
  group: GroupView;
  session: Session;
  playerId: string;
}) {
  // Which override the sub picker should write: the player's own slot, or — when they're
  // handing off a sub commitment — the slot of the player they were covering.
  const [flowFor, setFlowFor] = useState<string | undefined>();
  const [addingSub, setAddingSub] = useState(false);
  const online = useOnline();
  const setAvailable = useSetAvailability();
  const cancelSub = useDeleteOut();

  const roster = effectiveRoster(
    group.config,
    session,
    group.overrides,
    group.availabilities,
    group.fills,
  );
  const actions = cardActions(
    session,
    group.overrides,
    group.availabilities,
    playerId,
    group.fills,
  );
  const status = matchStatus(group, session, playerId);
  const mine = overrideFor(group, session.date, playerId);
  const commitment = subCommitmentFor(group, session.date, playerId);
  const pending = setAvailable.isPending || cancelSub.isPending;
  const error = (setAvailable.error ?? cancelSub.error) as Error | undefined;

  // A session can override the group's start time; the court booking still ends when the group's
  // booking ends, so a moved start shows as a shorter night rather than a shifted one.
  const heading = [
    formatDayShort(session.date),
    formatMonthDay(session.date),
    formatTimeRange(roster.time, group.config.endTime),
  ];

  function markAvailable(available: boolean) {
    setAvailable.mutate({
      key: group.key,
      input: {
        seasonId: view.seasonId,
        groupId: group.config.groupId,
        date: session.date,
        playerId,
        available,
      },
    });
  }

  function run(action: CardAction) {
    if (action === 'add-sub') {
      setAddingSub(true);
      return;
    }
    if (action === 'name-sub') {
      setFlowFor(playerId);
      return;
    }
    if (action === 'name-replacement') {
      setFlowFor(commitment?.outPlayerId);
      return;
    }
    if (action === 'cancel-sub') {
      cancelSub.mutate({
        key: group.key,
        input: {
          seasonId: view.seasonId,
          groupId: group.config.groupId,
          date: session.date,
          outPlayerId: playerId,
        },
      });
      return;
    }
    markAvailable(action === 'mark-available');
  }

  return (
    <article
      className={['match-card', status && `status-${status}`, roster.needed > 0 && 'needs-attention']
        .filter(Boolean)
        .join(' ')}
    >
      <div className="match-head">
        <h3 className="match-when">
          {heading.map((part, index) => (
            <span key={part}>
              {index > 0 && <span aria-hidden="true"> • </span>}
              {part}
            </span>
          ))}
        </h3>
        {actions.length > 0 && (
          <OptionsMenu
            actions={actions}
            disabled={!online || pending}
            onChoose={run}
            label={`Options for ${group.config.name}, ${heading[1]}`}
          />
        )}
      </div>

      {session.status === 'no-session' ? (
        <p className="no-tennis">No tennis — {session.note ?? 'off week'}</p>
      ) : (
        <>
          <h4>Lineup</h4>
          <ul className="match-players">
            {roster.playing.map((slot, index) => {
              const me = slot.playerId === playerId;
              return (
                <li
                  className={me ? 'is-me' : undefined}
                  key={slot.playerId ?? `${slot.name}-${index}`}
                >
                  <Name name={slot.name} isMe={me} />
                  {slot.inForName && (
                    <span className="note">
                      {' — in for '}
                      <Name name={slot.inForName} isMe={slot.inForPlayerId === playerId} />
                    </span>
                  )}
                </li>
              );
            })}
            {/* Rows rather than a count, so the lineup is always four lines long and the hole
                sits exactly where you'd look for the fourth name. Amber, because it is the
                one thing on the card that still wants doing — and the whole callout the card
                used to carry above this list, now that "Add sub" records an answer to it. */}
            {Array.from({ length: Math.max(0, roster.needed) }, (_, i) => (
              <li className="empty-slot" key={`empty-${i}`}>
                Player needed
              </li>
            ))}
          </ul>

          {/* Available first, then the ones who aren't coming — the order `effectiveRoster`
              hands them over in, so the picker in the out flow reads down the same list. */}
          <h4>Potential Subs</h4>
          <ul className="match-players">
            {roster.reserves.map((slot) => {
              const me = slot.playerId === playerId;
              return (
                <li className={me ? 'is-me' : undefined} key={slot.playerId}>
                  <Name name={slot.name} isMe={me} />
                  {slot.unavailable && <span className="note"> — not available</span>}
                </li>
              );
            })}
            {roster.reserves.length === 0 && (
              <li className="muted">Nobody — the whole group is on court.</li>
            )}
          </ul>

          {session.note && <p className="muted match-note">{session.note}</p>}
          {error && <p className="error">{error.message}</p>}
          {!online && actions.length > 0 && (
            <p className="muted match-note">Offline — changes are disabled until you reconnect.</p>
          )}
        </>
      )}

      {addingSub && (
        <AddSubFlow
          group={group}
          seasonId={view.seasonId}
          session={session}
          onClose={() => setAddingSub(false)}
        />
      )}

      {flowFor && (
        <OutFlow
          group={group}
          seasonId={view.seasonId}
          session={session}
          playerId={playerId}
          outPlayerId={flowFor}
          existing={flowFor === playerId ? mine : commitment}
          // Handing off is two facts: the slot is covered by somebody else now, and the player
          // handing it off isn't coming. The override goes first, because it's the write the
          // server can refuse — a rejection then leaves nothing changed at all.
          onSaved={flowFor === playerId ? undefined : () => markAvailable(false)}
          onClose={() => setFlowFor(undefined)}
        />
      )}
    </article>
  );
}

/**
 * The signed-in player's own name, wherever it appears. Bold is the whole treatment inside an
 * "in for {player}" annotation, where the name sits a few words into a sentence. On a row of
 * the player's own, the row treatment is what makes it findable, and the weight only keeps the
 * name ahead of the note.
 */
function Name({ name, isMe }: { name: string; isMe: boolean }) {
  return isMe ? <strong>{name}</strong> : <>{name}</>;
}

const LABELS: Record<CardAction, string> = {
  'name-sub': "I'm not available",
  'name-replacement': "I'm not available",
  'mark-unavailable': "I'm not available",
  'mark-available': "I'm available now",
  'cancel-sub': 'Cancel sub',
  'add-sub': 'Add sub',
};

/**
 * The ⋯ menu, one per card. Same disclosure rules as the site menu: dismissed by tapping off it
 * or pressing Escape, and focus is only pulled back to the button when it was inside the panel,
 * so a tap never leaves a focus ring behind.
 */
function OptionsMenu({
  actions,
  disabled,
  label,
  onChoose,
}: {
  actions: CardAction[];
  disabled: boolean;
  label: string;
  onChoose: (action: CardAction) => void;
}) {
  const [open, setOpen] = useState(false);
  const toggle = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') close();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  function close() {
    if (panel.current?.contains(document.activeElement)) toggle.current?.focus();
    setOpen(false);
  }

  return (
    <div className="card-options">
      <button
        ref={toggle}
        type="button"
        className="icon-button round"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <circle cx="4" cy="10" r="1.6" fill="currentColor" />
          <circle cx="10" cy="10" r="1.6" fill="currentColor" />
          <circle cx="16" cy="10" r="1.6" fill="currentColor" />
        </svg>
      </button>
      {open && <div className="menu-backdrop open" onClick={close} />}
      <div ref={panel} className={`card-menu${open ? ' open' : ''}`} inert={!open} role="menu">
        {actions.map((action) => (
          <button
            key={action}
            type="button"
            role="menuitem"
            onClick={() => {
              close();
              onChoose(action);
            }}
          >
            {LABELS[action]}
          </button>
        ))}
      </div>
    </div>
  );
}
